'use client';

import { AccordionContent, AccordionItem, AccordionTrigger, Root } from '@radix-ui/react-accordion';
import { cn, hasValue } from '@woonstad/helpers-utils';
import clsx from 'clsx';
import React, { AriaAttributes, useEffect, useState } from 'react';

import { DirectionsChevronDown } from '../../icons/directions';
import type { SVGComponent } from '../../types/SVGComponent';
import { Tag, TagProps } from '../tag/tag';
import { Typography } from '../typography/Typography';
import { BoxBase } from './BoxBase';

type CardExpanderVariants = 'default' | 'chevron';
type Props = AriaAttributes & {
  children: React.ReactNode | React.ReactNode[];
  icon?: SVGComponent;
  title: string;
  subtext?: string;
  tags?: Array<TagProps>;
  isDefaultOpen?: boolean;
  className?: string;
  variant?: CardExpanderVariants;
  onValueChange?: (value: string) => void;
  expanded?: boolean;
  value?: string;
};

const ACCORDION_ITEM = 'item';

/**
 * Based on Card Link
 *
 * Instead of opening a link, clicking the component expands the card and reveals additional content
 *
 * Has 2 variations:
 * - Visual: none
 * - Visual: icon
 */
export const CardExpander = ({
  children,
  icon: Icon,
  title,
  subtext,
  tags,
  isDefaultOpen,
  className,
  onValueChange,
  expanded,
  value = ACCORDION_ITEM,
}: Props) => {
  const [accordionState, setAccordionState] = useState(isDefaultOpen ? value : '');
  const isOpen = accordionState === value;

  useEffect(() => {
    if (hasValue(expanded) && expanded !== isOpen) {
      setAccordionState(expanded ? value : '');
    }
  }, [expanded, isOpen, value]);

  return (
    <Root
      collapsible
      defaultValue={isDefaultOpen ? value : undefined}
      type="single"
      className={className}
      value={accordionState}
      onValueChange={(value) => {
        if (hasValue(onValueChange)) {
          onValueChange(value);
        }

        setAccordionState(value);
      }}
    >
      <AccordionItem
        className="shadow-box transition-shadow hover:shadow-box-hover"
        value={value}
      >
        <AccordionTrigger
          className="group w-full cursor-pointer overflow-hidden"
        >
          <BoxBase className="flex w-full items-center p-4 text-left">
            {Icon && (
              <Icon
                className="mr-2 size-12 origin-top-left transition-transform"
              />
            )}

            <div className="flex origin-top-left flex-col transition-transform">
              {tags && tags.length > 0 && (
                <div className={cn('mb-1 flex flex-wrap gap-2')}>
                  {tags.map((tag) => (
                    <Tag
                      key={tag.children?.toString()}
                      {...tag}
                    />
                  ))}
                </div>
              )}

              <Typography
                variant="label-regular"
                type="span"
                className="text-card-link-enabled-label group-hover:text-textlink-enabled-on-light group-hover:underline"
              >
                {title}
              </Typography>
              {subtext && (
                <Typography
                  variant="content-body"
                  type="span"
                  className="text-card-link-enabled-sublabel"
                >
                  {subtext}
                </Typography>
              )}
            </div>

            <div className="ml-auto flex items-center gap-2 text-textlink-enabled-on-light">
              <Typography
                variant="label-regular"
                type="span"
              >
                {isOpen ? 'Sluiten' : 'Bekijken'}
              </Typography>
              <DirectionsChevronDown
                className={cn(
                  'size-6 transition-transform',
                  isOpen && 'rotate-180',
                )}
              />
            </div>
          </BoxBase>
        </AccordionTrigger>

        <AccordionContent
          className={cn(
            'relative -mt-px border border-card-link-enabled border-t-[white] bg-surface-default',
            {
              'animate-expand': isOpen,
              'animate-collapse pointer-events-none': !isOpen,
            },
          )}
        >
          <div
            className={clsx('p-4', {
              'duration-1000 animate-in fade-in': isOpen,
              'duration-100 animate-out fade-out fill-mode-forwards': !isOpen,
            })}
          >
            <hr className="w-full -translate-y-4 text-divider" />
            {children}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Root >
  );
};
